import React from 'react';
import { Space } from '../../types';
import SpaceOnboardingTooltip from './SpaceOnboardingTooltip';

interface SpaceCardWidgetProps {
  space: Space;
  onJoin: () => void;
  onView?: () => void;
  isUserInSpace?: boolean;
}

const SpaceCardWidget: React.FC<SpaceCardWidgetProps> = ({ 
  space, 
  onJoin, 
  onView,
  isUserInSpace = false
}) => {
  const participantCount = space.participants.length;
  const speakerCount = space.speakers.length;
  const maxParticipants = space.maxParticipants;

  return (
    <div className="relative mt-4 p-4 bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-xl border border-purple-500/30 backdrop-blur-sm">
      {/* Tooltip d'onboarding pour les nouveaux utilisateurs */}
      {!isUserInSpace && <SpaceOnboardingTooltip />}
      {/* Header Space */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
          <span className="text-purple-300 font-medium text-sm">
            🎙️ {space.isLive ? 'Discussion en cours' : 'Space programmé'}
          </span>
        </div>
        <span className="text-xs text-purple-400">
          {participantCount}/{maxParticipants}
        </span>
      </div>
      
      {/* Titre du Space */}
      {space.title && (
        <h4 className="text-emerald-100 font-medium text-sm mb-2 line-clamp-2">
          {space.title}
        </h4>
      )}

      {/* Info sur le rôle par défaut */}
      {!isUserInSpace && (
        <div className="mb-3 text-xs text-purple-300/80 bg-purple-900/20 rounded-lg p-2">
          💡 Vous rejoindrez en tant qu'auditeur. Levez la main pour devenir speaker !
        </div>
      )}

      {/* Preview des speakers */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className="flex -space-x-2">
            {space.speakers.slice(0, 3).map((speakerId, index) => (
              <div 
                key={speakerId} 
                className="w-8 h-8 bg-gradient-to-br from-emerald-600 to-cyan-600 rounded-full border-2 border-purple-900 flex items-center justify-center text-sm"
                style={{ zIndex: 10 - index }}
              >
                🐱‍🚀
              </div>
            ))}
            {speakerCount > 3 && (
              <div className="w-8 h-8 bg-gray-600 rounded-full border-2 border-purple-900 flex items-center justify-center text-xs text-gray-300">
                +{speakerCount - 3}
              </div>
            )}
          </div>
          <span className="text-sm text-purple-300">
            {speakerCount === 1 ? 'en discussion' : `${speakerCount} speakers`}
          </span>
        </div>

        {/* Tags */}
        {space.tags.length > 0 && (
          <div className="flex space-x-1">
            {space.tags.slice(0, 2).map(tag => (
              <span 
                key={tag}
                className="text-xs bg-purple-800/40 text-purple-300 px-2 py-1 rounded-full"
              >
                #{tag}
              </span>
            ))}
            {space.tags.length > 2 && (
              <span className="text-xs text-purple-400">
                +{space.tags.length - 2}
              </span>
            )}
          </div>
        )}
      </div>
      
      {/* Action buttons */}
      <div className="flex space-x-2">
        {isUserInSpace ? (
          <>
            <button 
              onClick={onView}
              className="flex-1 cosmic-button bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-sm py-2"
            >
              🎙️ Retourner au Space
            </button>
            <button className="cosmic-button-secondary px-3 py-2 text-sm">
              🔇
            </button>
          </>
        ) : (
          <>
            <button 
              onClick={onJoin}
              disabled={participantCount >= maxParticipants}
              className="flex-1 cosmic-button bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-sm py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {participantCount >= maxParticipants ? (
                '🚫 Complet'
              ) : (
                '🎙️ Rejoindre'
              )}
            </button>
            {onView && (
              <button 
                onClick={onView}
                className="cosmic-button-secondary px-3 py-2 text-sm"
                title="Écouter en tant qu'invité"
              >
                👁️ Preview
              </button>
            )}
          </>
        )}
      </div>

      {/* Barre de progression de remplissage */}
      <div className="mt-3">
        <div className="w-full bg-gray-700/50 rounded-full h-1.5">
          <div 
            className="bg-gradient-to-r from-purple-500 to-blue-500 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${Math.min((participantCount / maxParticipants) * 100, 100)}%` }}
          ></div>
        </div>
        <div className="flex justify-between text-xs text-purple-400 mt-1">
          <span>{participantCount} participants</span>
          <span>{maxParticipants - participantCount} places restantes</span>
        </div>
      </div>

      {/* Indicateur temps si le Space n'est pas encore live */}
      {!space.isLive && space.scheduledFor && (
        <div className="mt-2 text-xs text-purple-400 flex items-center">
          ⏰ Programmé pour {new Date(space.scheduledFor).toLocaleString('fr-FR', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      )}
    </div>
  );
};

export default SpaceCardWidget;
